import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Gift, Tag, ArrowRight, X } from "lucide-react";

/**
 * v3.99d.264 — Cross-site promotional strip banner.
 *
 * Embeds anywhere on CannaForge (Browse, Cart, ListingDetail, etc.) to
 * advertise live promotional packs + promo codes. Auto-rotates through
 * available offers every 6 seconds. Single-row, lightweight, dismissible
 * for the session.
 *
 * Data sources (both already exist):
 *   • GET /api/listings/promotional   — free / promo packs (v3.99d.263)
 *   • GET /api/promotions/public-active — promo codes (v3.99d.255)
 *
 * Renders nothing when there are no live offers — silently dissolves
 * (no "empty banner" footprint).
 */
export default function PromoStripBanner({ className = "", testid = "promo-strip-banner" }) {
  const [offers, setOffers] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("cf_promo_strip_dismissed") === "1"; }
    catch { return false; }
  });
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/listings/promotional").catch(() => ({ data: { items: [] } })),
      api.get("/promotions/public-active").catch(() => ({ data: { items: [] } })),
    ]).then(([listingsRes, promosRes]) => {
      if (cancelled) return;
      const out = [];
      // Promo packs first (free items > discount codes for hook value).
      for (const l of (listingsRes.data?.items || []).slice(0, 4)) {
        const isFree = !!l.is_free || Number(l.price || 0) <= 0;
        out.push({
          key: `pack-${l.id}`,
          kind: "pack",
          icon: Gift,
          badge: isFree ? "FREE" : "PROMO",
          title: l.title,
          subtitle: isFree ? `${l.seed_count || ""}-pack free sample` : `Promotional pack · $${Number(l.price).toFixed(2)}`,
          cta: isFree ? "Claim now" : "View details",
          to: `/listing/${l.slug || l.id}`,
        });
      }
      for (const p of (promosRes.data?.items || []).slice(0, 4)) {
        const value = (() => {
          const t = p.discount_type, v = Number(p.discount_value || 0);
          if (t === "percentage") return `${Math.round(v)}% OFF`;
          if (t === "fixed") return `$${v.toFixed(2)} OFF`;
          if (t === "free_shipping") return "FREE SHIPPING";
          return "PROMO";
        })();
        out.push({
          key: `promo-${p.id}`,
          kind: "code",
          icon: Tag,
          badge: value,
          title: p.name,
          subtitle: p.code
            ? `Code: ${p.code}`
            : "Auto-applied at checkout",
          cta: "Shop now",
          to: "/browse",
        });
      }
      setOffers(out);
    });
    return () => { cancelled = true; };
  }, []);

  // Rotate the active offer every 6s (only when there's more than one).
  useEffect(() => {
    if (offers.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % offers.length), 6000);
    return () => clearInterval(t);
  }, [offers.length]);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("cf_promo_strip_dismissed", "1"); } catch {}
  };

  if (dismissed || offers.length === 0) return null;
  const offer = offers[idx % offers.length];
  const Icon = offer.icon;

  return (
    // v3.99d.405 — Recolor: FREE PACKS strip is now GREEN (organic,
    // freebie feel) so it visually differs from the gold $10 signup
    // funds banner and the red Vault urgency banner. Single-color gold
    // stacking made all three banners bleed together — this gives each
    // its own semantic hue.
    <div
      className={`relative flex items-center gap-3 px-4 py-2.5 border ${className}`}
      style={{ background: "linear-gradient(90deg, rgba(20,120,80,0.14) 0%, rgba(20,120,80,0.03) 60%, transparent 100%)",
                borderColor: "var(--secondary)" }}
      data-testid={testid}>
      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border bg-black/40"
            style={{ color: "var(--secondary)", borderColor: "var(--secondary)" }}>
        <Icon className="w-3 h-3" />
        {offer.badge}
      </span>
      <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <Link
          to={offer.to}
          className="text-sm font-semibold truncate hover:underline"
          style={{ color: "var(--text)" }}
          data-testid={`${testid}-title-link`}>
          {offer.title}
        </Link>
        <span className="text-[11px] truncate"
              style={{ color: "var(--text-muted)" }}>
          {offer.subtitle}
        </span>
      </div>
      <Link
        to={offer.to}
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border hover:brightness-125 transition-all"
        style={{ color: "var(--secondary)",
                 borderColor: "var(--secondary)" }}
        data-testid={`${testid}-cta`}>
        {offer.cta}
        <ArrowRight className="w-3 h-3" />
      </Link>
      {offers.length > 1 && (
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0"
             data-testid={`${testid}-dots`}>
          {offers.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
                    className="w-1.5 h-1.5 rounded-full transition-all"
                    style={{
                      background: i === idx ? "var(--secondary)" : "var(--border)",
                      opacity: i === idx ? 1 : 0.5,
                    }}
                    aria-label={`Show offer ${i + 1}`}/>
          ))}
        </div>
      )}
      <button onClick={dismiss}
              className="flex-shrink-0 p-1 text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              aria-label="Dismiss promo strip"
              data-testid={`${testid}-dismiss`}>
        <X className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}
